import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Warehouse as WarehouseIcon, Plus, Package } from 'lucide-react';
import {
  useToast,
  PageHeader, Button, Input, EmptyState,
} from '../components/ui';

const Warehouse = () => {
  const { toast } = useToast();
  const [warehouses, setWarehouses] = useState([]);
  const [form, setForm] = useState({ name: '', location: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchWarehouses(); }, []);

  const fetchWarehouses = async () => {
    try {
      const res = await axios.get('/api/warehouse', { headers: { 'x-auth-token': localStorage.getItem('token') } });
      setWarehouses(res.data);
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post('/api/warehouse', form, { headers: { 'x-auth-token': localStorage.getItem('token') } });
      setForm({ name: '', location: '' });
      fetchWarehouses();
      toast.success('Warehouse added');
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Error adding warehouse');
    } finally {
      setSubmitting(false);
    }
  };

  const totalItems = warehouses.reduce((sum, w) => sum + (w.items?.length || 0), 0);

  return (
    <>
      <PageHeader
        title="Warehouse"
        subtitle="Bulk stock and inter-location transfers."
      />

      {/* Summary card */}
      <div className="bg-white rounded-lg border border-neutral-200 shadow-card p-5 mb-6 flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Stock across all warehouses</p>
          <p className="text-3xl font-bold text-neutral-900 tabular-nums mt-1">{totalItems}</p>
          <p className="text-xs text-neutral-500 mt-1">
            {warehouses.length} warehouse{warehouses.length === 1 ? '' : 's'}
          </p>
        </div>
        <div className="w-12 h-12 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center">
          <Package size={22} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Warehouses table */}
        <div className="lg:col-span-2 bg-white rounded-lg border border-neutral-200 shadow-card overflow-hidden">
          <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-neutral-800">All warehouses</h2>
            <span className="text-xs text-neutral-500">
              {warehouses.length} location{warehouses.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="app-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Location</th>
                  <th>Stock</th>
                </tr>
              </thead>
              <tbody>
                {warehouses.length === 0 && (
                  <tr>
                    <td colSpan="3">
                      <EmptyState
                        icon={WarehouseIcon}
                        title="No warehouses yet"
                        message="Add your first warehouse using the form on the right."
                      />
                    </td>
                  </tr>
                )}
                {warehouses.map(wh => (
                  <tr key={wh._id}>
                    <td className="font-medium">{wh.name}</td>
                    <td className="text-neutral-600">
                      {wh.location || <span className="text-neutral-400">—</span>}
                    </td>
                    <td className="tabular-nums">
                      {wh.items?.length || 0} item{(wh.items?.length || 0) === 1 ? '' : 's'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add warehouse form */}
        <div className="bg-white rounded-lg border border-neutral-200 shadow-card overflow-hidden h-fit">
          <div className="px-5 py-3 border-b border-neutral-200 flex items-center gap-2">
            <Plus size={16} className="text-primary-600" />
            <h2 className="text-sm font-semibold text-neutral-800">Add warehouse</h2>
          </div>
          <form onSubmit={handleSubmit} className="p-5 space-y-3">
            <Input
              label="Name"
              type="text"
              placeholder="e.g. Central Warehouse"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
            <Input
              label="Location"
              type="text"
              placeholder="City or address"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              required
            />
            <div className="pt-1">
              <Button type="submit" variant="primary" fullWidth loading={submitting}>
                Add warehouse
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default Warehouse;
